import { useContext, type KeyboardEvent } from 'react';
import { useMessages } from 'gt-react';
import type { Row, Table } from '@/lib/tanstack-table';
import { cn } from '@/lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { AssignmentLine } from 'src/api/rpc/hooks/procurement/useReplenishmentSuggestions';
import { CartInclusionContext } from './cartInclusionContext';
import { useLineBulkEditTargets } from './lineBulkEditContext';
import { getCartLineTableMeta } from './cartLineTableMeta';
import {
  approvalActionForKey,
  approvalActions,
  MIXED_APPROVAL_ACTIONS,
  type ApprovalAction,
} from './approvalState';
import {
  APPROVAL_ACTION_LABELS,
  approvalStateLabel,
  formatApprovalLabel,
} from './approvalStateLabels';

/** Cell style per state; credit-hold Trouble stands out from a buyer's hold. */
function approvalStateClass(line: AssignmentLine): string {
  switch (line.approvalState) {
    case 'approved':
      return 'border-emerald-200 bg-emerald-50 text-emerald-700';
    case 'auto_approved':
      return 'border-sky-200 bg-sky-50 text-sky-700';
    case 'trouble':
      return line.troubleReason === 'credit_hold'
        ? 'border-rose-200 bg-rose-50 text-rose-700'
        : 'border-amber-300 bg-amber-100 text-amber-800';
    case 'rejected':
      return 'border-gray-200 bg-gray-50 text-gray-400 line-through';
    case 'released':
      return 'border-emerald-200 bg-emerald-50 text-emerald-700';
    default:
      // Unapproved: the default, a quiet grey U.
      return 'border-gray-200 text-gray-400 hover:border-gray-300';
  }
}

/**
 * The line's approval state, in the board's first column: a fixed-width
 * one-letter code (see approvalStateLabels) that opens a menu to change it.
 * Keyboard: the keys match the codes, A (approve), U (unapprove), T (Trouble)
 * and R (reject / restore); Enter opens the menu. Clicks stay in the cell: the
 * column is exempt from row selection (assignmentBoardRowClick).
 */
export function ApprovalStateCell({
  row,
  table,
  summary,
}: {
  row: Row<AssignmentLine>;
  table: Table<AssignmentLine>;
  /** A need row's `approved/open` count, shown in place of the code. */
  summary?: string;
}) {
  const meta = getCartLineTableMeta(table);
  const context = useContext(CartInclusionContext);
  const onSetApproval = context?.onSetApproval ?? meta.onSetApproval;
  const editTargets = useLineBulkEditTargets();
  const line = row.original;
  // A need row is pending while any of its SO lines is saving: Trash and
  // Restore record the expanded SO ids, not the need's.
  const pending = (
    summary == null ? [line.suggestionId] : editTargets(line.suggestionId)
  ).some((id) => meta.pendingStatusIds.has(id));
  // A need row whose SO lines disagree can move all of them to any state.
  const actions =
    summary == null ? approvalActions(line) : MIXED_APPROVAL_ACTIONS;
  const m = useMessages();
  const label = approvalStateLabel(line);
  const description = formatApprovalLabel(label, m);

  const run = (action: ApprovalAction) => {
    const targets = editTargets(line.suggestionId);
    if (action === 'rejected' || action === 'restore') {
      const status = action === 'rejected' ? 'rejected' : 'pending';
      if (targets.length > 1 && meta.onSetStatuses) {
        meta.onSetStatuses(targets, status);
        return;
      }
      for (const suggestionId of targets) {
        meta.onSetStatus(suggestionId, status);
      }
      return;
    }
    onSetApproval(line, action);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.metaKey || event.ctrlKey || event.altKey) return;
    const action = approvalActionForKey(event.key, line);
    if (action == null || !actions.includes(action)) return;
    event.preventDefault();
    event.stopPropagation();
    run(action);
  };

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          disabled={pending || actions.length === 0}
          title={description}
          aria-label={description}
          onClick={(event) => event.stopPropagation()}
          onKeyDown={onKeyDown}
          className={cn(
            // Fixed width, so a state change never resizes the column.
            'inline-flex h-6 shrink-0 items-center justify-center rounded border font-mono text-xs font-semibold leading-none transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50',
            summary == null ? 'w-7' : 'min-w-7 px-1 tabular-nums',
            approvalStateClass(line),
          )}
        >
          {summary ?? (label.code == null ? null : m(label.code))}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        onClick={(event) => event.stopPropagation()}
      >
        {actions.map((action) => (
          <DropdownMenuItem key={action} onSelect={() => run(action)}>
            {formatApprovalLabel(APPROVAL_ACTION_LABELS[action], m)}
            <DropdownMenuShortcut>{shortcutFor(action)}</DropdownMenuShortcut>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** The line's approval state by name, styled as its board cell. */
export function ApprovalStateChip({ line }: { line: AssignmentLine }) {
  const m = useMessages();
  return (
    <span
      className={cn(
        'shrink-0 rounded border px-1.5 text-[11px] font-medium leading-4',
        approvalStateClass(line),
      )}
    >
      {m(approvalStateLabel(line).name)}
    </span>
  );
}

function shortcutFor(action: ApprovalAction): string {
  switch (action) {
    case 'approved':
      return 'A';
    case 'unapproved':
      return 'U';
    case 'trouble':
      return 'T';
    default:
      return 'R';
  }
}
